import React, { useEffect, useState } from "react";
import { Alert, Box, CircularProgress, Typography } from "@mui/material";
import { Link, useNavigate } from "react-router-dom";
import { useTheme } from "@mui/material/styles";
import useMediaQuery from "@mui/material/useMediaQuery";
import { getFirebaseAuthModule } from "../../Repository/Authentication/Firebase";
import { useAuthUser } from "../../Context/AuthUserContext";
import {
  clearPendingQrReturn,
  resolveCamiCodeFromLink,
  verifyCamiCodeScan,
} from "../../Business/CamiCodeScanBusiness";
import CamiCard from "./CamiCard";

/**
 * Handles QR links opened directly in the browser (same flow as in-app scan).
 * Falls back to the QR card UI when there is no playable video yet.
 */
export default function QrCodeLinkEntry({ redirPath = "/", showCardFallback = true }) {
  const theme = useTheme();
  const navigate = useNavigate();
  const smallDevices = useMediaQuery(theme.breakpoints.down("md"));
  const { profile, authReady, isLoggedIn, profileLoading, refreshProfile } = useAuthUser();
  const [phase, setPhase] = useState("loading");
  const [errorMessage, setErrorMessage] = useState("");

  const getOptionalAuthHeaders = () => {
    const token = localStorage.getItem("JWTTOKEN");
    return token ? { Authorization: `Bearer ${token}` } : {};
  };

  useEffect(() => {
    let cancelled = false;

    async function runLinkFlow() {
      if (!authReady) {
        return;
      }
      if (isLoggedIn && profileLoading && !profile) {
        return;
      }

      try {
        const urlParams = new URLSearchParams(window.location.search);
        const viewCardOnly = urlParams.get("view") === "card";

        const resolved = await resolveCamiCodeFromLink(window.location, getOptionalAuthHeaders);
        if (!resolved.camiCode || !resolved.password) {
          if (!cancelled) {
            setPhase(showCardFallback ? "card" : "error");
            if (!showCardFallback) {
              setErrorMessage("This link is missing QR code details.");
            }
          }
          return;
        }

        if (viewCardOnly) {
          if (!cancelled) {
            setPhase("card");
          }
          return;
        }

        const auth = getFirebaseAuthModule();
        let activeProfile = profile;
        if (isLoggedIn && (!activeProfile || activeProfile.id == null)) {
          try {
            activeProfile = (await refreshProfile()) || activeProfile;
          } catch {
            /* keep existing profile */
          }
        }
        const authUser =
          auth.currentUser ||
          (isLoggedIn && activeProfile?.email ? { email: activeProfile.email } : null);

        const outcome = await verifyCamiCodeScan({
          camiCode: resolved.camiCode,
          password: resolved.password,
          getAuthHeaders: getOptionalAuthHeaders,
          authUser,
          profile: activeProfile,
          redirPath,
          showOwnerOptions: true,
        });

        if (cancelled) return;

        if (outcome.action === "watch" || outcome.action === "upload") {
          clearPendingQrReturn();
          navigate(outcome.url, { replace: true });
          return;
        }
        if (outcome.action === "owner-options") {
          clearPendingQrReturn();
          // Show the same Record / Upload / Watch chooser as in-app scan.
          navigate(
            `/scan-qr?c=${encodeURIComponent(resolved.camiCode)}&p=${encodeURIComponent(resolved.password)}`,
            { replace: true }
          );
          return;
        }
        if (outcome.action === "login") {
          if (isLoggedIn || auth.currentUser) {
            const retryOutcome = await verifyCamiCodeScan({
              camiCode: resolved.camiCode,
              password: resolved.password,
              getAuthHeaders: getOptionalAuthHeaders,
              authUser:
                auth.currentUser ||
                (activeProfile?.email ? { email: activeProfile.email } : null),
              profile: activeProfile,
              redirPath,
              showOwnerOptions: true,
            });
            if (retryOutcome.action === "owner-options") {
              clearPendingQrReturn();
              navigate(
                `/scan-qr?c=${encodeURIComponent(resolved.camiCode)}&p=${encodeURIComponent(resolved.password)}`,
                { replace: true }
              );
              return;
            }
            if (retryOutcome.action === "upload" || retryOutcome.action === "watch") {
              clearPendingQrReturn();
              navigate(retryOutcome.url, { replace: true });
              return;
            }
            setErrorMessage(
              retryOutcome.message ||
                "There is no video on this QR code and you do not have permission to upload to it."
            );
            setPhase("error");
            return;
          }
          navigate("/login", {
            state: outcome.loginState,
            replace: true,
          });
          return;
        }
        if (outcome.action === "error") {
          setErrorMessage(outcome.message || "You cannot upload to this QR code.");
          setPhase("error");
          return;
        }
        setErrorMessage("Could not open this QR link.");
        setPhase("error");
      } catch (error) {
        if (!cancelled) {
          setErrorMessage(error?.message || "Could not open this QR link.");
          setPhase("error");
        }
      }
    }

    runLinkFlow();
    return () => {
      cancelled = true;
    };
  }, [
    navigate,
    redirPath,
    showCardFallback,
    profile,
    authReady,
    isLoggedIn,
    profileLoading,
    refreshProfile,
  ]);

  if (phase === "loading" || !authReady || (isLoggedIn && profileLoading && !profile)) {
    return (
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          minHeight: "50vh",
          pt: smallDevices ? 8 : 3,
          gap: 2,
        }}
      >
        <CircularProgress />
        <Typography sx={{ color: theme.palette.text.secondary }}>Opening QR link...</Typography>
      </Box>
    );
  }

  if (phase === "card" && showCardFallback) {
    return <CamiCard />;
  }

  return (
    <Box sx={{ px: 2, py: smallDevices ? 8 : 4 }}>
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          minHeight: "40vh",
        }}
      >
        <Alert severity="error">{errorMessage || "Could not open this QR link."}</Alert>
      </Box>
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          gap: 1.5,
          mt: 2,
        }}
      >
        <Link to="/login" style={{ color: theme.palette.primary.main, textDecoration: "none" }}>
          Log in
        </Link>
        <Link to="/register" style={{ color: theme.palette.primary.main, textDecoration: "none" }}>
          Register
        </Link>
        <Link to="/scan-qr" style={{ color: theme.palette.primary.main, textDecoration: "none" }}>
          Scan a QR code
        </Link>
      </Box>
    </Box>
  );
}
